// React imports
import { FC, useEffect, useMemo, useReducer } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// Model imports
import { State } from '@/model/State.model';
import { Animal } from '@/model/Animal.model';

// Component imports
import Loading from '@/components/Loading';
import { closeModal, openModal } from '@/components/Shared/methods';
import Banner from '@/components/UI/Banner';
import Button from '@/components/UI/Button';
import APITable from '@/components/UI/Table/Reusable/APITable';

// Utility imports
import { updateColumns } from '@/components/UI/Table/Reusable/Cache.utils';
import { successToast } from '@/helpers';
import Http from '@/support/http';
import { api as apiRoute } from '@/support/route';
import { RouteService } from '@/support/RouteService';

// Local imports
import {
  animalsHaveStudies,
  generateColumns,
  generateFilterOptions,
  reducer,
  selectedAnimalsStudyIds,
  validateManageAnimalsSelection,
} from './Animals.utils';
import { createMultiSelectDoubleEncodingProcessor } from '@/utils/TableFilter.utils';

// TYPES AND INTERFACES

interface AnimalsProps {
  settings?: {
    tables?: {
      colonyAnimals?: {
        columns?: Record<string, boolean>;
      };
    };
  };
}

interface FilterOption {
  name: string;
  value: string;
  options?: Array<Record<string, unknown>>;
  operations?: Array<{
    name: string;
    value: string;
    options: Array<unknown>;
  }>;
  fetchFromApi?: {
    placeholder: string;
    url: string;
  };
}

interface AnimalsState {
  loading: boolean;
  updating: boolean;
  error: boolean;
  data: Array<Animal>;
  columns: Array<Record<string, unknown>>;
  searchQuery: string;
  filterOptions: Array<FilterOption>;
  filters: Array<unknown>;
  filterMatch: boolean;
  selectedRows: Record<string, boolean>;
  total_pages: number;
  current_page: number;
  per_page: number;
  order: 'asc' | 'desc';
  sort: Array<string>;
  metadata: Array<unknown>;
}

export type AnimalsAction =
  | { type: 'SET_LOADING'; data: boolean }
  | { type: 'SET_UPDATING'; data: boolean }
  | { type: 'SET_ERROR'; data: boolean }
  | { type: 'SET_DATA'; data: Array<Animal> }
  | { type: 'SET_COLUMNS'; data: Array<Record<string, unknown>> }
  | { type: 'SET_FILTER_OPTIONS'; data: Array<FilterOption> };

// CONSTANTS

const INITIAL_PAGE_SIZES = [50, 100, 200, 300];
const DEFAULT_SORT_BY = { id: 'id', desc: true };
const INCLUDE_PARAMS = 'cage,metadata,study_link,terminated_at_data';
const SEARCH_PLACEHOLDER = 'Search by name';

const DEFAULT_SELECTION_PROPS = {
  hasWritePermission: true,
  isNotDeceased: true,
  hasActiveStudy: true,
  hasIdenticalStudy: false,
};

const Animals: FC<AnimalsProps> = ({ settings = {} }) => {
  // STATE INITIALIZATION AND HOOKS
  const initialState = useMemo(
    (): AnimalsState => ({
      loading: true,
      updating: false,
      error: false,
      data: [],
      columns: generateColumns({ metadata: [] }),
      searchQuery: '',
      filterOptions: generateFilterOptions({ fields: [], metadata: [] }),
      filters: [],
      filterMatch: true,
      selectedRows: {},
      total_pages: 1,
      current_page: 1,
      per_page: 10,
      order: 'desc',
      sort: ['id'],
      metadata: [],
    }),
    []
  );

  const [state, dispatch] = useReducer(reducer, initialState);
  const { columns, loading, updating, error, filterOptions, metadata } = state;
  const storeDispatch = useDispatch();
  const navigate = useNavigate();
  const reduxTableName = 'colonyAnimals';

  // Feature flags
  const { features } = useSelector(({ team: { features } }: State) => ({
    features,
  }));
  const atunesIntegrationEnabled = features.atunes_colony_management_integration === true;

  // EFFECTS
  useEffect(() => {
    const updatedColumns = updateColumns(reduxTableName, columns, settings);
    dispatch({
      type: 'SET_COLUMNS',
      data: updatedColumns,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings?.tables]);

  // HELPER FUNCTIONS
  const fetchFilterOptions = async () => {
    try {
      const { data } = await Http.get(apiRoute('animals.filterOptions'));
      dispatch({
        type: 'SET_FILTER_OPTIONS',
        data: generateFilterOptions(data),
      });
      dispatch({
        type: 'SET_METADATA',
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        data: data.metadata || [],
      });
      dispatch({
        type: 'SET_COLUMNS',
        data: generateColumns(data),
      });
    } catch (data) {
      dispatch({ type: 'SET_ERROR', data });
    } finally {
      dispatch({ type: 'SET_LOADING', data: false });
    }
  };

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  /**
   * Handles deletion of multiple animals
   * @param callback Function to call on successful deletion
   * @param selectedAnimals Array of animals to delete
   * @param reasonForDelete Optional reason for deletion
   */
  const handleDelete = async (
    callback: (message: string) => void,
    selectedAnimals: Array<Animal>,
    reasonForDelete?: string
  ) => {
    try {
      const url = RouteService.api({
        endpoint: 'DELETE /api/v1/animals/destroy-many-colony',
        path: undefined,
      }).url.href;
      await Http.delete(url, {
        data: {
          animal_api_ids: selectedAnimals.map((s) => s.api_id),
          reason_for_change: reasonForDelete,
        },
      });
      return callback('Successfully deleted!');
    } catch (data) {
      dispatch({ type: 'SET_ERROR', data });
    }
  };

  const defaultSelectionProps = DEFAULT_SELECTION_PROPS;

  // BULK ACTIONS AND EVENT HANDLERS

  /**
   * Generates bulk actions for selected animals in the table
   * @param param0 Object containing table props with selected rows and fetch function
   * @returns Array of action objects for the bulk action dropdown
   */
  const bulkActions = ({
    useTableProps: {
      selectedFlatRows,
      apiTable: { refetchTableData },
    },
  }: {
    useTableProps: {
      selectedFlatRows: Array<{ original: Animal }>;
      apiTable: { refetchTableData: () => Promise<void> };
    };
  }) => {
    const selectedAnimals = selectedFlatRows.map((s) => s.original);
    const callback = async (res: string) => {
      closeModal(storeDispatch);
      successToast(res);
      await refetchTableData();
    };
    const actions = [
      {
        name: 'Assign identifiers',
        key: 'assign_identifiers',
        ...validateManageAnimalsSelection({ ...defaultSelectionProps, hasIdenticalStudy: true }, selectedAnimals),
        action: () => {
          const animals = selectedAnimals.map(({ id, name, alt_ids: altIds, cage }) => ({
            id,
            name,
            alt_ids: altIds,
            cage,
          }));

          storeDispatch({
            type: 'OPEN_MODAL',
            modal: 'ASSIGN_IDENTIFIERS',
            props: {
              animals,
              handleCallback: callback,
              closeModal: () => closeModal(storeDispatch),
            },
          });
        },
      },
      {
        name: 'Edit Animal details',
        key: 'edit_details',
        ...validateManageAnimalsSelection(defaultSelectionProps, selectedAnimals),
        action: () => {
          navigate(RouteService.web({ route: 'colony.edit.animals' }).route, {
            state: { animals: selectedAnimals },
          });
        },
      },
      {
        name: 'Move cage',
        ...validateManageAnimalsSelection({ ...defaultSelectionProps, hasIdenticalStudy: true }, selectedAnimals),
        action: () => {
          storeDispatch({
            type: 'OPEN_MODAL',
            modal: 'MOVE_SUBJECT_SEARCH',
            props: {
              selectedAnimals,
              selectedAnimalsStudyIds: () => selectedAnimalsStudyIds(selectedAnimals),
              animalsHaveStudies: animalsHaveStudies(selectedAnimals),
              handleCallback: callback,
            },
          });
        },
      },
    ];

    if (!atunesIntegrationEnabled) {
      actions.push(
        {
          name: 'Mark as deceased',
          ...validateManageAnimalsSelection(defaultSelectionProps, selectedAnimals),
          action: () =>
            openModal('BULK_EUTHANISE_SUBJECTS', storeDispatch, {
              subjects: selectedAnimals,
              closeModal: () => closeModal(storeDispatch),
              handleCallback: callback,
            }),
        },
        {
          name: 'Delete',
          ...validateManageAnimalsSelection(
            { hasWritePermission: true, isNotDeceased: false, hasActiveStudy: true, hasIdenticalStudy: false },
            selectedAnimals
          ),
          action: () =>
            openModal('CONFIRM_DELETE_SUBJECTS', storeDispatch, {
              subject: selectedAnimals,
              onClick: (reasonForDelete?: string) => handleDelete(callback, selectedAnimals, reasonForDelete),
            }),
        }
      );
    }
    return actions;
  };

  // RENDER
  return (
    <div className="ma4">
      {/* eslint-disable-next-line @typescript-eslint/strict-boolean-expressions */}
      {error && (
        <Banner critical dismiss={false} className="mw6 ma3">
          <h3 className="f5 normal lh-title pb2">There was an error fetching animal information</h3>
          <p className="f6 pb3 lh-copy">If this error persists try again later or contact support.</p>
          <Button outline critical url={'mailto:support@benchling.com'}>
            Contact support
          </Button>
        </Banner>
      )}
      {/* eslint-disable-next-line @typescript-eslint/strict-boolean-expressions */}
      <div className={`animals ${!loading && updating ? 'ui__disabled' : ''}`}>
        {/* eslint-disable-next-line @typescript-eslint/strict-boolean-expressions */}
        {loading ? (
          <div style={{ height: 530 }}>
            <Loading txt="Fetching animals..." />
          </div>
        ) : (
          <APITable
            apiInfo={{
              type: 'internalApi',
              route: RouteService.api({
                endpoint: 'GET /api/v1/animals',
                path: {} as any,
              }).url,
            }}
            includeParam={INCLUDE_PARAMS}
            defaultSortBy={DEFAULT_SORT_BY}
            columns={(columns as any).filter((col: { header?: string }) => col.header)}
            bulkActions={bulkActions}
            filterOptions={filterOptions}
            showViewSelector
            searchPlaceholderText={SEARCH_PLACEHOLDER}
            pageSizes={INITIAL_PAGE_SIZES}
            reduxTableName={reduxTableName}
            data-testid="animals-api-table"
            customFilterProcessor={createMultiSelectDoubleEncodingProcessor(metadata ?? [])}
          />
        )}
      </div>
    </div>
  );
};

export default Animals;
